import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { AuthWrapper } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { useUpdatePermissions } from "@/features/auth/hooks/useAuth"

/**
 * PermissionSetupScreen - Pixel-perfect implementation matching image copy 6/7.png
 */
export function PermissionSetupScreen() {
  const { t } = useTranslation("onboarding")
  const navigate = useNavigate()
  const updatePermissionsMutation = useUpdatePermissions()

  const [permissions, setPermissions] = useState({
    calendarEnabled: true,
    notificationsEnabled: true,
    contactsEnabled: true,
    locationEnabled: true,
  })

  const isLoading = updatePermissionsMutation.isPending

  const handleSubmit = async () => {
    try {
      await updatePermissionsMutation.mutateAsync(permissions)
      // Navigate to welcome screen on success
      navigate("/welcome")
    } catch (error) {
      // Error is handled by the hook and displayed via toast
      console.error("Permissions update error:", error)
    }
  }

  const toggleItems = [
    {
      key: "calendarEnabled" as const,
      label: t("permissions.calendar"),
      description: t("permissions.calendarDescription"),
    },
    {
      key: "notificationsEnabled" as const,
      label: t("permissions.notifications"),
      description: t("permissions.notificationsDescription"),
    },
    {
      key: "contactsEnabled" as const,
      label: t("permissions.contacts"),
      description: t("permissions.contactsDescription"),
    },
    {
      key: "locationEnabled" as const,
      label: t("permissions.location"),
      description: t("permissions.locationDescription"),
    },
  ]

  return (
    <AuthWrapper maxWidth="640px" className="px-7.5 pt-12.5 pb-13.75">
      {/* Title */}
      <div className="text-center mb-10">
        <h2 className="text-2xl leading-normal font-semibold text-[#F2F0EBE6]">
          {t("permissions.title")}
        </h2>
      </div>

      {/* Permission Toggles */}
      <div className="space-y-5 mb-10">
        {toggleItems.map(item => (
          <div key={item.key} className="rounded-lg flex justify-between items-start p-0">
            {/* Text Content - Top */}
            <div className="flex flex-col gap-1.25">
              <h3 className="font-semibold text-sm leading-6.25 text-primary">{item.label}</h3>
              <p className="text-[13px] leading-5 text-[#F2F0EB66]">{item.description}</p>
            </div>

            {/* Toggle - Centered */}
            <div className="flex items-center">
              <Switch
                checked={permissions[item.key]}
                onCheckedChange={(checked: boolean) =>
                  setPermissions(prev => ({
                    ...prev,
                    [item.key]: checked,
                  }))
                }
                className="h-[30px]! w-[50px]!"
                disabled={isLoading}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Start Your Journey Button */}
      <div className="flex items-center justify-center ">
        <Button
          type="button"
          variant="primary-white"
          size="md"
          className="py-3.75 h-[50px] uppercase w-full font-semibold md:max-w-[220px]"
          isLoading={isLoading}
          fullWidth
          onClick={handleSubmit}
        >
          {t("permissions.enableAll")}
        </Button>
      </div>
    </AuthWrapper>
  )
}
